import { action, computed, makeObservable } from 'mobx';

import { ProviderUid } from '@gem/call/CallProviders/callProviders.types';
import { MemberDevice } from '@gem/call/CallStore/Member/MemberDevice';
import { ClientRole } from '@gem/call/constants';

import { Call } from '../Call';

interface IOpponentConstructorParams {
  call: Call;
  uid: ProviderUid;
}

export class MeOpponent {
  uid: ProviderUid = '';
  call: Call;
  hasLeft = false;
  sortIndex = 0;
  role = ClientRole.Host;

  constructor({ call, uid }: IOpponentConstructorParams) {
    makeObservable(this);
    this.call = call;
    this.uid = uid;
  }

  get isMe(): boolean {
    return true;
  }

  get isPlayingVideo(): boolean {
    return this.call.meStore.isPlayingVideo;
  }

  get hasAudio(): boolean {
    return !this.call.meStore.isAudioMuted;
  }

  get hasVideo(): boolean {
    return !this.call.meStore.isVideoMuted;
  }

  get volumeLevel(): number {
    return this.call.meStore.isSpeaking ? 100 : 0;
  }

  get memberDevice(): MemberDevice | null {
    const memberDevice = this.uid ? this.call.members.getMemberDeviceByUid(this.uid) : null;

    if (memberDevice) {
      return memberDevice;
    }

    return (
      this.call.members.memberDevicesList.find((memberDevice) => {
        return memberDevice.shareScreenUid === this.uid;
      }) || null
    );
  }

  get handRaisedPosition(): number | null {
    if (this.call.members.handsQueue.isShowPosition) {
      return this.call.members.handsQueue.getPosition(this.uid);
    }

    return null;
  }

  setVolumeLevel(level: number): void {
    this.call.meStore.setIsPlayingVideo(level >= 50);
  }

  setIsPlayingVideo(isPlayingVideo: boolean): void {
    this.call.meStore.setIsPlayingVideo(isPlayingVideo);
  }

  setHasAudio(hasAudio: boolean): void {
    this.call.meStore.setIsAudioMuted(!hasAudio);
  }

  setHasVideo(hasVideo: boolean): void {
    this.call.meStore.setIsVideoMuted(!hasVideo);
  }

  setSortIndex(sortIndex: number): void {
    this.sortIndex = sortIndex;
  }

  setRole(role: ClientRole): void {
    this.role = role;
  }

  @action setUid(uid: ProviderUid): void {
    this.uid = uid;
  }

  @computed
  get isActiveSpeaker(): boolean {
    return this.volumeLevel > 1 && this.hasAudio;
  }

  @computed
  get isSharedScreen(): boolean {
    return false;
  }
}
